/**
 * WhatsApp WABA TPA types and constants.
 * Meta Business API. Based on whatsapp-things.js.
 */

// ── API Response Types ──────────────────────────────────────────

/** Meta Graph API error object for send failures. */
export interface WhatsAppSendError {
  message?: string;
  code?: number;
  error_data?: { details?: string };
}

export interface WhatsAppSendResponse {
  messages: Array<{ id: string }>;
  /** `wa_id` is the phone; `user_id` is Meta's business-scoped user ID (BSUID)
   *  for username-only users whose phone number is hidden. */
  contacts: Array<{ wa_id?: string; user_id?: string }>;
  /** CF failure envelope — Meta's Graph API error usually arrives nested one
   *  level deeper ({ error: { error: { message, code, error_data } } }); some
   *  paths return it flat ({ error: { message, ... } }). */
  error?: WhatsAppSendError & { error?: WhatsAppSendError };
}

export interface WhatsAppWABAInfoResponse {
  id?: string;
  ownership_type: string;
  phone_numbers: {
    data: Array<{
      display_phone_number: string;
      verified_name: string;
      id: string;
      quality_rating?: string;
    }>;
  };
  on_behalf_of_business_info?: {
    name: string;
    id?: string;
  };
}

export interface WhatsAppTemplatesResponse {
  data: Array<WhatsAppRawTemplate>;
  paging?: { next?: string; cursors?: { before?: string; after?: string } };
}

export interface WhatsAppRawTemplate {
  id: string;
  name: string;
  language: string;
  status: string;
  category: string;
  components: Array<WhatsAppRawTemplateComponent>;
}

export interface WhatsAppRawTemplateComponent {
  type: string;
  format?: string;
  text?: string;
  buttons?: Array<{
    type: string;
    text: string;
    url?: string;
    phone_number?: string;
    example?: string[];
  }>;
  example?: {
    header_handle?: string[];
    header_text?: string[];
    body_text?: string[][];
  };
}

// ── Catalog & Product Types ─────────────────────────────────────

export interface WhatsAppCatalogResponse {
  product_catalogs?: {
    data: Array<WhatsAppCatalog>;
  };
  owned_product_catalogs?: {
    data: Array<WhatsAppCatalog>;
  };
}

export interface WhatsAppCatalog {
  id: string;
  name: string;
  product_count: number;
  products?: {
    data: Array<WhatsAppProduct>;
  };
}

export interface WhatsAppProduct {
  id: string;
  retailer_id: string;
  name: string;
  short_description?: string;
  price?: string;
  currency?: string;
  image_url?: string;
  url?: string;
  availability?: string;
  visibility?: string;
}

// ── Interactive Message Types ───────────────────────────────────

export type WhatsAppInteractiveType = 'catalog_message' | 'product' | 'product_list';

export interface WhatsAppInteractivePayload {
  type: WhatsAppInteractiveType;
  header?: { type: string; text?: string };
  body?: { text: string };
  footer?: { text: string };
  action: Record<string, unknown>;
}

// ── Webhook / Incoming Message Types ────────────────────────────

export type WAMessageOrigin = 'whatsapp' | 'ulgebra_chat_ui_waba' | 'ua-msg-system';

/** Raw incoming webhook message from WhatsApp */
export interface WhatsAppIncomingWebhookMessage {
  _uaOriginService: 'whatsapp';
  id: string;
  _id?: string;
  downloadedMediaURL?: string;
  isUnRead?: boolean;
  entry: Array<{
    changes: Array<{
      field: string;
      value: {
        messaging_product: string;
        metadata: {
          display_phone_number: string;
          phone_number_id: string;
        };
        contacts: Array<{
          profile: { name: string; username?: string };
          /** Phone number — omitted by Meta for username-only users */
          wa_id?: string;
          /** Business-scoped user ID (e.g. "US.1349…") — set for username users */
          user_id?: string;
        }>;
        messages: Array<{
          id: string;
          /** Phone number — omitted by Meta for username-only users */
          from?: string;
          /** BSUID of the sender — set for username users */
          from_user_id?: string;
          timestamp: string;
          type: string;
          text?: { body: string };
          image?: { id: string; mime_type?: string; sha256?: string; caption?: string };
          video?: { id: string; mime_type?: string; sha256?: string; caption?: string };
          audio?: { id: string; mime_type?: string; sha256?: string };
          document?: { id: string; mime_type?: string; sha256?: string; filename?: string };
          sticker?: { id: string; mime_type?: string; sha256?: string };
          location?: { latitude: number; longitude: number; name?: string; address?: string };
          contacts?: Array<Record<string, unknown>>;
          interactive?: { type: string;[key: string]: unknown };
          order?: {
            catalog_id: string;
            product_items: Array<{
              product_retailer_id: string;
              quantity: number;
              item_price: number;
              currency: string;
            }>;
          };
          referral?: {
            source_url?: string;
            source_type?: string;
            headline?: string;
            body?: string;
            media_type?: string;
            image_url?: string;
            video_url?: string;
          };
          reaction?: { message_id: string; emoji: string };
          button?: { text: string; payload?: string };
          context?: { message_id: string; from?: string };
          errors?: Array<{ code: number; title: string; message?: string }>;
        }>;
        to?: string;
      };
    }>;
  }>;
  [key: string]: unknown;
}

/** Outgoing message sent via the WABA chat UI */
export interface WhatsAppOutgoingUIMessage {
  _uaOriginService: 'ulgebra_chat_ui_waba';
  id: string;
  _id?: string;
  _uaCreatedTime: number;
  from?: string;
  to?: string;
  type: string;
  channelId?: string;
  text?: { body: string };
  image?: { link: string; caption?: string };
  video?: { link: string; caption?: string };
  audio?: { link: string };
  document?: { link: string; filename?: string; caption?: string };
  template?: {
    name: string;
    language: { code: string; policy?: string };
    components: Array<Record<string, unknown>>;
  };
  interactive?: WhatsAppInteractivePayload;
  header?: { type: string; text?: string };
  body?: { text: string };
  footer?: { text: string };
  context?: { message_id: string };
  source?: {
    type: string;
    email?: string;
    name?: string;
    id?: string;
    pic?: string;
    uaApp?: string;
    uaAppSaaSOrgID?: string;
    uaAppSaaSUserId?: string;
  };
  resolvedTemplateMessageText?: string;
  isPrivateNote?: boolean;
  status?: string;
  status_sent_time?: number;
  status_delivered_time?: number;
  status_read_time?: number;
  status_failed_time?: number;
  status_error_time?: number;
  status_failed_text?: string;
  status_failed_code?: number;
  status_failed_href?: string;
  status_failed_details?: string;
  reactionText?: string;
  isUnRead?: boolean;
  replySuggestionText?: string;
  replySuggestions?: string[];
  [key: string]: unknown;
}

/** System message (assignee-update, label-update, subject-update, chat-reminder, optout-update) */
export interface WhatsAppSystemMessage {
  _uaOriginService: 'ua-msg-system';
  id?: string;
  _id?: string;
  type: 'assignee-update' | 'label-update' | 'subject-update' | 'chat-reminder' | 'optout-update';
  performer: string;
  assigneeEmail?: string;
  label?: string;
  subject?: string;
  /** optout-update (KAN-155): true = opted out, false = opted back in. */
  optOut?: boolean;
  /** optout-update: the keyword the customer sent, e.g. "STOP". */
  keyword?: string;
  time: number;
  _conversationId?: string;
  isPrivate?: boolean;
  author?: { name: string; email?: string; pic?: string; uid?: string };
  [key: string]: unknown;
}

export type WhatsAppRawMessage =
  | WhatsAppIncomingWebhookMessage
  | WhatsAppOutgoingUIMessage
  | WhatsAppSystemMessage;

/** Product display item used in message rendering */
export interface WAProductDisplayItem {
  image_url?: string;
  title: string;
  sub_title?: string;
  url?: string;
}

// ── Firestore Conversation Doc ──────────────────────────────────

export interface WhatsAppFirestoreConversation {
  id: string;
  contactId: string;
  channelId: string;
  messagesCount: number;
  lastUpdatedTime: number;
  lastMessage?: Record<string, unknown>;
  subject?: string;
  assigneeEmail?: string;
  label?: string;
  pinnedMessage?: string;
  saasModuleId?: string;
  latestExistingRecordName?: string;
  [key: string]: unknown;
}

// ── Constants ───────────────────────────────────────────────────

export const WHATSAPP_GRAPH_API = 'https://graph.facebook.com/v22.0';

export const FACEBOOK_OAUTH_URL = 'https://www.facebook.com/v22.0/dialog/oauth';

export const FACEBOOK_OAUTH_SCOPES = 'whatsapp_business_messaging,whatsapp_business_management,email';

export const FACEBOOK_OAUTH_REDIRECT_URI = 'https://sms.ulgebra.com/oauth-home';

export const FACEBOOK_DEFAULT_APP_ID = '1239348770257388';

// ── Facebook Embedded Signup ────────────────────────────────────

export const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

// v3 config was '979484930240046'.
export const FACEBOOK_EMBEDDED_SIGNUP_CONFIG_ID = '27664256176572104';

/**
 * TEMPORARY: Facebook restricted our app's WhatsApp Embedded Signup, so the
 * FB.login() onboarding flow is disabled. While this is `false`, the auth UI
 * mimics the legacy page — the user enters their own Facebook App ID and
 * authorizes via OAuth. Flip back to `true` to restore Embedded Signup.
 */
export const WHATSAPP_EMBEDDED_SIGNUP_ENABLED = true;

// ── Facebook JS SDK TypeScript declarations ─────────────────────

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    FB?: {
      init(config: {
        appId: string;
        autoLogAppEvents: boolean;
        xfbml: boolean;
        version: string;
      }): void;
      login(
        callback: (response: {
          authResponse?: { code?: string; accessToken?: string; userID?: string };
          status?: string;
        }) => void,
        options?: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          extras: Record<string, string>;
        },
      ): void;
    };
  }
}

/**
 * Get the Facebook App ID used to build the OAuth authorization URL.
 *
 * Always returns the Ulgebra Facebook App ID (`1239348770257388`) — the
 * OAuth flow is registered against this app on the Meta side, so any
 * other value would fail the authorization redirect. The legacy
 * `customFacebookAPPID` localStorage override is intentionally ignored.
 */
export function getFacebookAppId(): string {
  return FACEBOOK_DEFAULT_APP_ID;
}

export const CLIENT_IDS: Record<string, string> = {
  whatsappforhubspotcrm: '5ce62ce4-e697-423c-b878-a2b28f9dd297',
  whatsappforpipedrive: 'd688695f917bf352',
  whatsappforbitrix24: 'app.5fca3756b0c5c2.28053584',
};

/** Media type detection from MIME prefix */
export function getWAMediaType(mimeType: string): 'image' | 'video' | 'audio' | 'document' {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'document';
}

/** Currency code to symbol mapping */
export function getCurrencySymbol(code: string): string {
  const map: Record<string, string> = {
    USD: '$', EUR: '\u20AC', GBP: '\u00A3', INR: '\u20B9',
    JPY: '\u00A5', AUD: 'A$', CAD: 'C$', SGD: 'S$',
    AED: 'AED', SAR: 'SAR', BRL: 'R$',
  };
  return map[code?.toUpperCase()] ?? code ?? '';
}

// ── Workflow Code Templates ─────────────────────────────────────

export const WORKFLOW_CODES: Record<string, Record<string, unknown>> = {
  TEXT_MESSAGE: {
    messaging_product: 'whatsapp',
    whatsAppSenderID: 'FILL_HERE',
    recipient_type: 'individual',
    from: 'FILL_HERE',
    to: 'FILL_HERE',
    type: 'text',
    text: { preview_url: false, body: 'FILL_HERE' },
    source: { type: 'uaapp-workflow' },
    channel: 'WhatsApp',
    module: 'FILL_HERE',
    recordId: 'FILL_HERE',
    ulgebra_webhook_authtoken: null,
  },
  MEDIA_MESSAGE: {
    messaging_product: 'whatsapp',
    whatsAppSenderID: 'FILL_HERE',
    recipient_type: 'individual',
    from: 'FILL_HERE',
    to: 'FILL_HERE',
    type: 'image',
    image: { link: 'FILL_HERE_Attachment_URL', caption: 'FILL_HERE_YOUR_CAPTION' },
    source: { type: 'uaapp-workflow' },
    channel: 'WhatsApp',
    module: 'FILL_HERE',
    recordId: 'FILL_HERE',
    ulgebra_webhook_authtoken: null,
  },
  TEMPLATE_MESSAGE: {
    messaging_product: 'whatsapp',
    whatsAppSenderID: 'FILL_HERE',
    recipient_type: 'individual',
    from: 'FILL_HERE',
    to: 'FILL_HERE',
    type: 'template',
    template: {
      name: 'FILL_HERE',
      language: { policy: 'deterministic', code: 'en' },
      components: [{ type: 'body', parameters: [{ type: 'text', text: 'FILL_HERE' }] }],
    },
    source: { type: 'uaapp-workflow' },
    channel: 'WhatsApp',
    module: 'FILL_HERE',
    recordId: 'FILL_HERE',
    ulgebra_webhook_authtoken: null,
  },
  MEDIA_TEMPLATE_MESSAGE: {
    messaging_product: 'whatsapp',
    whatsAppSenderID: 'FILL_HERE',
    recipient_type: 'individual',
    from: 'FILL_HERE',
    to: 'FILL_HERE',
    type: 'template',
    template: {
      name: 'FILL_HERE',
      language: { policy: 'deterministic', code: 'en' },
      components: [
        {
          type: 'header',
          parameters: [{ type: 'image', image: { link: 'FILL_HERE_Attachment_URL' } }],
        },
        {
          type: 'body',
          parameters: [{ type: 'text', text: 'FILL_HERE_PARAM_VALUE' }],
        },
      ],
    },
    source: { type: 'uaapp-workflow' },
    channel: 'WhatsApp',
    module: 'FILL_HERE',
    recordId: 'FILL_HERE',
    ulgebra_webhook_authtoken: null,
  },
  incoming: {
    ulgebra_webhook_authtoken: null,
  },
};

export const FIELD_MAPPING_SCHEMA: Record<string, string[]> = {
  phone: ['$.to', '$.from'],
  message: ['$.text.body'],
};

// ── Calling API Types ───────────────────────────────────────────

export type WhatsAppCallStatus =
  | 'ringing'
  | 'accepted'
  | 'rejected'
  | 'terminated'
  | 'ongoing'
  | 'missed';

export type WhatsAppCallDirection = 'inbound' | 'outbound';

export type WhatsAppCallIconVisibility = 'DEFAULT' | 'DISABLE_ALL';

export type WhatsAppCallSettingsStatus = 'ENABLED' | 'DISABLED';

export interface WhatsAppCallBusinessHours {
  weekly_schedule?: Array<{
    day: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
    start_time: string; // "HHMM" format e.g. "0900"
    end_time: string;   // "HHMM" format e.g. "1700"
  }>;
  holiday_schedule?: Array<{
    date: string;       // "YYYY-MM-DD"
    start_time: string; // "HHMM"
    end_time: string;   // "HHMM"
  }>;
}

export interface WhatsAppCallSIPConfig {
  status: 'ENABLED' | 'DISABLED';
  servers?: Array<{
    hostname: string;
    port: number;
    request_uri_user_params?: Record<string, string>;
  }>;
}

export interface WhatsAppCallSettings {
  status: WhatsAppCallSettingsStatus;
  call_icon_visibility?: WhatsAppCallIconVisibility;
  call_icons?: {
    voice_call?: WhatsAppCallIconVisibility;
    video_call?: WhatsAppCallIconVisibility;
  };
  business_hours?: WhatsAppCallBusinessHours;
  callback_permission_status?: 'ENABLED' | 'DISABLED';
  sip?: WhatsAppCallSIPConfig;
}

export interface WhatsAppCallSettingsResponse {
  calling?: WhatsAppCallSettings;
  success?: boolean;
}

/** Business-initiated call request payload */
export interface WhatsAppCallInitiatePayload {
  messaging_product: 'whatsapp';
  to: string;                    // E.164 recipient number
  action: 'connect';
  session: {
    sdp_type: 'offer';
    sdp: string;                 // RFC 8866 SDP
  };
  biz_opaque_callback_data?: string;
}

export interface WhatsAppCallInitiateResponse {
  messaging_product: 'whatsapp';
  calls: Array<{ id: string }>;
}

/** Accept incoming call payload */
export interface WhatsAppCallAcceptPayload {
  messaging_product: 'whatsapp';
  action: 'accept';
  session: {
    sdp_type: 'answer';
    sdp: string;
  };
}

export interface WhatsAppCallAcceptResponse {
  messaging_product: 'whatsapp';
  success: boolean;
}

/** Webhook: Call event entry (inside value.calls[]) */
export interface WhatsAppCallWebhookEntry {
  id: string;              // unique call ID (e.g. "wacid.ABGGFjFVU2AfAgo6V")
  to: string;              // callee number
  from: string;            // caller number — omitted by Meta for username-only callers
  /** Caller's business-scoped user ID (BSUID) — set for username users */
  from_user_id?: string;
  event: 'connect' | 'terminate';
  timestamp: string;
  direction: 'BUSINESS_INITIATED' | 'USER_INITIATED';
  session?: {
    sdp_type: 'offer' | 'answer';
    sdp: string;           // RFC 8866 SDP
  };
  biz_opaque_callback_data?: string;
  // Terminate-specific fields
  status?: 'FAILED' | 'COMPLETED';
  start_time?: string;
  end_time?: string;
  duration?: number;
}

/** Webhook: Call status entry (inside value.statuses[]) */
export interface WhatsAppCallStatusWebhook {
  id: string;              // unique call ID
  timestamp: string;
  type: 'call';
  status: 'RINGING' | 'ACCEPTED' | 'REJECTED';
  recipient_id: string;
  biz_opaque_callback_data?: string;
}

/** Combined webhook value object for calls field */
export interface WhatsAppCallWebhookValue {
  messaging_product: 'whatsapp';
  metadata: {
    display_phone_number: string;
    phone_number_id: string;
  };
  contacts?: Array<{ wa_id: string }>;
  calls?: WhatsAppCallWebhookEntry[];
  statuses?: WhatsAppCallStatusWebhook[];
  errors?: Array<{
    code: number;
    message: string;
    href?: string;
    error_data?: { details: string };
  }>;
}

/** Active WhatsApp call state (used in store) */
export interface WhatsAppActiveCall {
  callId: string;
  direction: WhatsAppCallDirection;
  contactNumber: string;
  phoneNumberId: string;
  status: WhatsAppCallStatus | 'connecting' | 'idle';
  startTime: number;
  duration: number;
  isMuted: boolean;
  /** SDP offer from the incoming call webhook — needed for accept */
  sdpOffer?: string;
}

/** Call permission request interactive message payload */
export interface WhatsAppCallButtonPayload {
  messaging_product: 'whatsapp';
  recipient_type: 'individual';
  /** Phone number. For a username-only user, send `recipient` (BSUID) instead. */
  to?: string;
  recipient?: string;
  type: 'interactive';
  interactive: {
    type: 'call_permission_request';
    body: { text: string };
    action: {
      name: 'call_permission_request';
    };
  };
}

/** Incoming webhook reply when user acts on a call permission request */
export interface WhatsAppCallPermissionReply {
  response: 'accept' | 'reject';
  is_permanent?: boolean;
  expiration_timestamp?: string;
  response_source: 'user_action' | 'automatic';
}

/** Call permission stored on conversation document — raw Facebook payload */
export type WhatsAppCallPermission = WhatsAppCallPermissionReply;

// ── Business Profile Types ───────────────────────────────────

export interface WhatsAppBusinessProfileResponse {
  data: Array<{
    about?: string;
    address?: string;
    description?: string;
    email?: string;
    profile_picture_url?: string;
    websites?: string[];
    vertical?: string;
  }>;
}

/** Payload for updating WhatsApp Business Profile via POST /{phone_number_id}/whatsapp_business_profile */
export interface WhatsAppUpdateBusinessProfilePayload {
  messaging_product: 'whatsapp';
  about?: string;
  address?: string;
  description?: string;
  email?: string;
  websites?: string[];
  vertical?: string;
  profile_picture_url?: string;
}

// ── Template Management Types ────────────────────────────────

/** Payload for creating a WhatsApp template via POST /{wabaId}/message_templates */
export interface WhatsAppCreateTemplatePayload {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  components: WhatsAppTemplateComponentPayload[];
  allow_category_change?: boolean;
}

/** Component structure for create/edit payloads */
export interface WhatsAppTemplateComponentPayload {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  buttons?: Array<{
    type: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY';
    text: string;
    url?: string;
    phone_number?: string;
    example?: string[];
  }>;
  example?: {
    header_handle?: string[];
    header_text?: string[];
    body_text?: string[][];
  };
}

/** Payload for editing a WhatsApp template via POST /{templateId} */
export interface WhatsAppEditTemplatePayload {
  components: WhatsAppTemplateComponentPayload[];
}

/** Response from template create/edit/delete operations */
export interface WhatsAppTemplateActionResponse {
  id?: string;
  status?: string;
  category?: string;
  success?: boolean;
  error?: {
    message: string;
    type: string;
    code: number;
    error_subcode?: number;
    fbtrace_id?: string;
  };
}

/** Available WhatsApp template languages (common subset) */
export const WHATSAPP_TEMPLATE_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'es', label: 'Spanish' },
  { code: 'pt_BR', label: 'Portuguese (BR)' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ar', label: 'Arabic' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'zh_CN', label: 'Chinese (Simplified)' },
  { code: 'zh_TW', label: 'Chinese (Traditional)' },
  { code: 'ru', label: 'Russian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'nl', label: 'Dutch' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
];

/**
 * Message events a user can turn on/off on the incoming settings page, per
 * target: Inbox (message ticks) and Webhook (forwarding webhooks).
 * Ids are Meta's status values ('incoming' is ours). 'incoming' always goes
 * to the inbox, so it has no inbox toggle.
 */
export const WHATSAPP_MESSAGE_EVENTS: Array<{ id: string; label: string; description: string; inboxToggle: boolean }> = [
  { id: 'incoming', label: 'Incoming message received', description: 'A new message from a customer', inboxToggle: false },
  { id: 'sent', label: 'Sent', description: 'Message sent to WhatsApp', inboxToggle: true },
  { id: 'delivered', label: 'Delivered', description: "Message delivered to the customer's phone", inboxToggle: true },
  { id: 'read', label: 'Read', description: 'Customer read the message', inboxToggle: true },
  { id: 'failed', label: 'Failed', description: 'Message could not be sent or delivered', inboxToggle: true },
];

/** Template categories for the creation form */
export const WHATSAPP_TEMPLATE_CATEGORIES = [
  { value: 'MARKETING' as const, label: 'Marketing', description: 'Promotions, offers, updates' },
  { value: 'UTILITY' as const, label: 'Utility', description: 'Order updates, account alerts' },
  { value: 'AUTHENTICATION' as const, label: 'Authentication', description: 'OTPs, verification codes' },
];
